import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { prettyChannelName } from "../../../channels/utils";
import { useFollowUpScheduleText } from "../../hooks/use-follow-up-schedule-text";
import type { FollowUpSchemaType } from "../../reducer/schema";
import type { FollowUpMomentDetails } from "../../server/types";
import { FollowUpRecurringWarning } from "../follow-up-recurring-warning";

interface DialogProps {
  reason: NonNullable<FollowUpSchemaType["reason"]>;
  schedule: FollowUpSchemaType["schedule"];
  channel: FollowUpSchemaType["channel"];
  moment: FollowUpMomentDetails;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
}

export const FollowUpPublishDialog: FC<DialogProps> = ({
  reason,
  schedule,
  channel,
  moment,
  open,
  onConfirm,
  onCancel,
  saving,
}) => {
  const { t } = useTranslation();

  const reasonText = useMemo(
    () =>
      reason.type === "no_interaction"
        ? t(`Anyone who hasn't opened or interacted with "${moment.title}"`)
        : t(
            `Anyone who hasn't completed the survey "${moment.surveys?.find((x) => x.id === reason.survey_id)?.title}"` +
              ` from the Communication "${moment.title}"`,
          ),
    [reason, moment.title, moment.surveys, t],
  );

  const scheduleText = useFollowUpScheduleText(schedule, moment);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Publish Follow-Up?</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Stack direction="column" spacing={1}>
            <Typography variant="subtitle2">Recipients:</Typography>
            <Typography variant="body2">{reasonText}</Typography>
          </Stack>
          <FollowUpRecurringWarning details={moment} />
          <Stack direction="column" spacing={1}>
            <Typography variant="subtitle2">Channel:</Typography>
            <Stack direction="row" spacing={0.5}>
              <ChannelIcon channel={channel} fontSize="small" />
              <Typography variant="body2">{prettyChannelName(channel)}</Typography>
            </Stack>
          </Stack>
          <Typography variant="body2">{scheduleText}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          color="primary"
          data-analytics-id="follow-up-publish-cancel-button"
          variant="text"
          loading={saving}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          data-analytics-id="follow-up-publish-confirm-button"
          variant="contained"
          loading={saving}
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
};
